import { QUAL_CONFIG } from "@/lib/qualification";

export const WINDOW_HEAT = { HOT: 12, WARM: 6, COOLING: -4, STALE: -12 };
export const FRESH_HEAT = { HOT: 8, FRESH: 4, AGING: -4, STALE: -10 };

export const CAPACITY = { agents: 4, highPriorityThreshold: 80 };

export const POLICY_CONFIG = {
  lead: "Broker A — FTD Europe · VIP 85+ · PRIORITY 70–84 · STANDARD 0–69",
  client: "Broker A — Retention · HEALTHY 78+ · WATCH 65–77 · RETENTION PRIORITY 55–64 · CRITICAL <55",
};

export const VALIDATION = {
  leads: "10,000",
  result: "High-priority cohort showed stronger FTD conversion than the comparison cohort",
  note: "Demo / simulated validation data. Production process: historical broker data → backtest qualification logic → compare ranked cohorts → tune thresholds → controlled validation → monitor outcomes.",
};

export const POLICY_HEALTH = {
  status: "STABLE",
  note: "No drift detected across source mix or score calibration. Recalibration recommended if conversion patterns shift.",
};

export const MEASUREMENT_MODE = "Assisted Attribution — controlled validation available in production deployments";

export const DEDUP_NOTE = "Event deduplication active — duplicate or late-arriving events are applied once, ordered by event timestamp.";

export const SIGNAL_SOURCES = {
  new_registration: "CRM", source_quality_ok: "Acquisition Feed", platform_engaged: "Web / Trading Event",
  funding_intent_high: "Payment Provider", payment_friction_technical: "Payment Provider", live_engagement: "Messaging Channel",
  ftd_confirmed: "Trading Platform", email_engaged: "Messaging Channel", platform_active: "Web / Trading Event",
  funding_link_clicked: "Messaging Channel", deposit_initiated: "Payment Provider", repeated_payment_failure: "Payment Provider",
  automation_limit: "Policy Engine", issuer_block_identified: "Human Review", alternative_route_success: "Payment Provider",
  strong_engagement: "Web / Trading Event", commercial_intent_high: "CRM", kyc_active_friction: "KYC Provider",
  kyc_link_engaged: "KYC Provider", kyc_progressing: "KYC Provider", high_value_intent: "Payment Provider",
  kyc_review_required: "KYC Provider", compliance_restriction: "Policy Engine", sla_breach: "Workflow Engine",
  healthy_baseline: "Trading Platform", login_normal_band: "Trading Platform", activity_declining: "Trading Platform",
  login_frequency_reduced: "Trading Platform", funding_behaviour_changed: "Payment Provider", message_opened: "Messaging Channel",
  client_login_detected: "Trading Platform", hv_inactivity: "Trading Platform", automated_attempt_failed: "Messaging Channel",
  rm_contact_positive: "Human Review", ftd_opportunity: "CRM", conversion_window: "CRM", suppression_active: "Policy Engine",
  high_value_client: "CRM", withdrawal_detected: "Payment Provider", regulated_journey: "Policy Engine",
  complaint_risk: "Messaging Channel", sentiment_recovered: "Messaging Channel", withdrawal_released: "Payment Provider",
  active_account: "CRM", withdrawal_status_request: "Messaging Channel", client_acknowledged: "Messaging Channel",
  channel_matrix_mapped: "CRM", whatsapp_engagement: "Messaging Channel", observation_window: "Policy Engine",
  organic_deposit_intent: "Payment Provider", organic_ftd: "Trading Platform",
};

const PERM_ALL = { WhatsApp: "Allowed", SMS: "Allowed", Email: "Allowed", Call: "Allowed" };

export const OPS_CONFIG = {
  ftd: {
    propensity: 84, valuePotential: "HIGH", expectedOpportunity: "HIGH",
    freshness: { registered: "3 minutes ago", level: "HOT" },
    bestChannel: { channel: "WhatsApp", reasons: ["WhatsApp response history", "Mobile registration", "Channel available + consent valid"] },
    bestTime: "Now — EU desk active",
    routing: { team: "Italian FTD Desk", agent: "Italian-speaking senior sales" },
    identity: { status: "New lead — no duplicate signals", action: null },
    contactHistory: { attempts: 1, window: "current session", status: "CONTACT NOW" },
    permissions: PERM_ALL,
    sourceHealth: { today: "NORMAL", note: "No anomalies in today's flow from this affiliate" },
    economics: { sourceCost: "HIGH", efficiency: "STRONG" },
    whyNow: "High deposit intent + payment failure appears technical + historically strong affiliate + fresh registration + EU desk available now.",
  },
  nudge: {
    propensity: 71, valuePotential: "MEDIUM", expectedOpportunity: "MEDIUM",
    freshness: { registered: "38 minutes ago", level: "FRESH" },
    bestChannel: { channel: "Email", reasons: ["Welcome email opened", "No WhatsApp consent", "German desk active"] },
    bestTime: "Now — EU desk active",
    routing: { team: "German FTD Desk", agent: "German-speaking sales" },
    identity: { status: "New lead — no duplicate signals", action: null },
    contactHistory: { attempts: 1, window: "current session", status: "CONTACT NOW" },
    permissions: { ...PERM_ALL, Call: "Restricted" },
    sourceHealth: { today: "NORMAL", note: "No anomalies in today's flow from this affiliate" },
    economics: { sourceCost: "MEDIUM", efficiency: "STRONG" },
    whyNow: "Funding link clicked and deposit initiated — engagement is rising while the German desk is active.",
  },
  "payments-esc": {
    propensity: 91, valuePotential: "VERY HIGH", expectedOpportunity: "VERY HIGH",
    freshness: { registered: "43 days ago — returned today", level: "AGING" },
    bestChannel: { channel: "Call", reasons: ["Phone validated", "Card-issuer issue requires conversation", "Email deliverability confirmed"] },
    bestTime: "Now — UK desk active",
    routing: { team: "Payments Support", agent: "Senior payments specialist" },
    identity: { status: "Existing lead detected — previous record 43 days ago", signals: ["Same phone", "Same device", "Previous CRM record"], action: "Continue existing lifecycle — context linked, not treated as new" },
    contactHistory: { attempts: 3, window: "40 minutes", status: "HUMAN REVIEW", note: "Contact fatigue risk — human review before further automated contact" },
    permissions: PERM_ALL,
    sourceHealth: { today: "NORMAL", note: "No anomalies in today's flow from this affiliate" },
    economics: { sourceCost: "HIGH", efficiency: "STRONG" },
    whyNow: "Returning high-value prospect with £8,000 intent — failures are issuer-side and a payments specialist is available now.",
  },
  kyc: {
    propensity: 88, valuePotential: "MEDIUM", expectedOpportunity: "HIGH",
    freshness: { registered: "19 hours ago", level: "FRESH" },
    bestChannel: { channel: "Email", reasons: ["KYC link clicked via email", "No WhatsApp consent", "EU desk active"] },
    bestTime: "Now — EU desk active",
    routing: { team: "KYC Support", agent: "Spanish-speaking KYC assistance" },
    identity: { status: "New lead — no duplicate signals", action: null },
    contactHistory: { attempts: 1, window: "19 hours", status: "CONTACT NOW" },
    permissions: { ...PERM_ALL, Call: "Restricted" },
    sourceHealth: { today: "NORMAL", note: "No anomalies in today's flow from this affiliate" },
    economics: { sourceCost: "MEDIUM", efficiency: "STRONG" },
    whyNow: "High commercial intent with an active KYC attempt — document friction is resolvable while motivation is high.",
  },
  compliance: {
    propensity: 82, valuePotential: "VERY HIGH", expectedOpportunity: "HIGH",
    freshness: { registered: "51 minutes ago", level: "FRESH" },
    bestChannel: { channel: "Human-only review", reasons: ["Governance requires authorised review", "No automated interpretation permitted"] },
    bestTime: "Compliance desk — next authorised slot",
    routing: { team: "KYC / Compliance Review", agent: "Authorised compliance officer" },
    identity: { status: "Previous demo account on record", action: "Context linked — no duplicate treatment" },
    contactHistory: { attempts: 0, window: "—", status: "HUMAN REVIEW" },
    permissions: { WhatsApp: "Allowed", SMS: "Restricted", Email: "Allowed", Call: "Restricted" },
    sourceHealth: null,
    economics: null,
    whyNow: "Strong commercial intent ($25,000) with concurrent review requirement — authorised compliance review is the only correct next step.",
    conflictNote: "Strong commercial intent with concurrent review requirement.",
  },
  healthy: {
    propensity: null, valuePotential: "MEDIUM", expectedOpportunity: "—",
    freshness: { registered: "Client for 8 months — active today", level: "FRESH" },
    bestChannel: { channel: "None needed", reasons: ["No open requirement", "Routine monitoring only"] },
    bestTime: "—",
    routing: { team: "None — standard monitoring", agent: "—" },
    identity: { status: "Existing funded client — verified", action: null },
    contactHistory: { attempts: 0, window: "—", status: "NO FURTHER ACTION" },
    permissions: PERM_ALL,
    sourceHealth: null,
    economics: null,
    whyNow: "Healthy active client within normal bands — no action required.",
  },
  retention: {
    propensity: null, valuePotential: "HIGH", expectedOpportunity: "HIGH",
    freshness: { registered: "No login for 9 days", level: "AGING" },
    bestChannel: { channel: "WhatsApp", reasons: ["WhatsApp response history", "Italian language match", "Consent valid"] },
    bestTime: "Now — EU desk active",
    routing: { team: "Retention Desk", agent: "Italian-speaking retention specialist" },
    identity: { status: "Existing funded client — verified", action: null },
    contactHistory: { attempts: 1, window: "current session", status: "CONTACT NOW" },
    permissions: { ...PERM_ALL, Call: "Restricted" },
    sourceHealth: null,
    economics: null,
    whyNow: "Returning high-value client with declining activity — immediate retention window before full dormancy.",
  },
  "hv-esc": {
    propensity: null, valuePotential: "VERY HIGH", expectedOpportunity: "VERY HIGH",
    freshness: { registered: "14 days inactive", level: "STALE" },
    bestChannel: { channel: "Call", reasons: ["RM direct line", "High-value relationship", "Automated attempt unengaged"] },
    bestTime: "Now — RM available",
    routing: { team: "Retention Desk", agent: "Senior relationship manager — high-value" },
    identity: { status: "Existing funded client — verified", action: null },
    contactHistory: { attempts: 1, window: "7 days ago", status: "CONTACT NOW" },
    permissions: PERM_ALL,
    sourceHealth: null,
    economics: null,
    whyNow: "$62,000-balance client inactive for 14 days with the automated attempt unengaged — human RM window is open now.",
  },
  suppressed: {
    propensity: 58, valuePotential: "MEDIUM", expectedOpportunity: "LOW",
    freshness: { registered: "3 days ago", level: "AGING" },
    bestChannel: { channel: "No outbound communication permitted", reasons: ["Communication permission unavailable", "Suppression registry active"] },
    bestTime: "Wait — suppression active",
    routing: { team: "None — suppression registry", agent: "—" },
    identity: { status: "New lead — location consistency under review", action: null },
    contactHistory: { attempts: 0, window: "—", status: "SUPPRESS AUTOMATION" },
    permissions: { WhatsApp: "Restricted", SMS: "Restricted", Email: "Restricted", Call: "Restricted" },
    permissionsNote: "Outbound communication: not permitted",
    sourceHealth: { today: "ANOMALOUS", note: "Abnormal duplicate rate from this affiliate today — review recommended (not a fraud determination)" },
    economics: { sourceCost: "LOW", efficiency: "WEAK" },
    whyNow: "FTD opportunity detected, but communication permission is unavailable — governance suppresses outreach despite commercial intent.",
    conflictNote: "Commercial opportunity held back by communication governance.",
  },
  multilingual: {
    propensity: 77, valuePotential: "MEDIUM", expectedOpportunity: "MEDIUM",
    freshness: { registered: "1 hour ago", level: "FRESH" },
    bestChannel: { channel: "WhatsApp", reasons: ["WhatsApp consent valid", "Arabic language match", "SMS not consented"] },
    bestTime: "Now — Arabic desk active",
    routing: { team: "Arabic FTD Desk", agent: "Arabic-speaking sales" },
    identity: { status: "New lead — no duplicate signals", action: null },
    contactHistory: { attempts: 1, window: "current session", status: "CONTACT NOW" },
    permissions: { WhatsApp: "Allowed", SMS: "Restricted", Email: "Allowed", Call: "Restricted" },
    sourceHealth: { today: "NORMAL", note: "No anomalies in today's flow from this affiliate" },
    economics: { sourceCost: "MEDIUM", efficiency: "MEDIUM" },
    whyNow: "WhatsApp engagement detected in Arabic with the funding link viewed — the Arabic desk is available now.",
  },
  organic: {
    propensity: 79, valuePotential: "MEDIUM", expectedOpportunity: "HIGH",
    freshness: { registered: "6 hours ago", level: "FRESH" },
    bestChannel: { channel: "None — observation window", reasons: ["Organic journey — no O.N.E.Tech contact", "Welcome journey only"] },
    bestTime: "Next desk opening (registered 02:40 UK)",
    routing: { team: "None — organic journey", agent: "—" },
    identity: { status: "New lead — no duplicate signals", action: null },
    contactHistory: { attempts: 0, window: "—", status: "NO FURTHER ACTION" },
    permissions: PERM_ALL,
    sourceHealth: null,
    economics: null,
    whyNow: "Organic £3,000 FTD completed without intervention — attribution integrity preserved; welcome journey only.",
  },
  withdrawal: {
    propensity: null, valuePotential: "HIGH", expectedOpportunity: "—",
    freshness: { registered: "Active today", level: "FRESH" },
    bestChannel: { channel: "Email", reasons: ["Approved status communication", "Governance-approved channel"] },
    bestTime: "Now — per withdrawal policy",
    routing: { team: "Payments Support", agent: "Withdrawals desk" },
    identity: { status: "Existing funded client — verified", action: null },
    contactHistory: { attempts: 1, window: "current session", status: "CONTACT NOW" },
    permissions: PERM_ALL,
    permissionsNote: "Service communication only during withdrawal processing",
    sourceHealth: null,
    economics: null,
    whyNow: "High-value client awaiting withdrawal — approved status communication only; governance controls active.",
  },
  "withdrawal-pt": {
    propensity: null, valuePotential: "MEDIUM", expectedOpportunity: "—",
    freshness: { registered: "Active today", level: "FRESH" },
    bestChannel: { channel: "WhatsApp", reasons: ["Client's acknowledged channel", "Approved status communication"] },
    bestTime: "Now — per withdrawal policy",
    routing: { team: "Payments Support", agent: "Withdrawals desk" },
    identity: { status: "Existing funded client — verified", action: null },
    contactHistory: { attempts: 1, window: "current session", status: "CONTACT NOW" },
    permissions: PERM_ALL,
    permissionsNote: "Service communication only during withdrawal processing",
    sourceHealth: null,
    economics: null,
    whyNow: "Routine withdrawal status request — approved communication, processing normally.",
  },
};

export function rankScore(qual, ops) {
  const intel = QUAL_CONFIG[qual.id]?.intel;
  let s = qual.priority * 0.55 + qual.score * 0.3;
  s += WINDOW_HEAT[qual.window] || 0;
  s += FRESH_HEAT[ops?.freshness?.level] || 0;
  if (intel) {
    if (intel.eligibility.status !== "ELIGIBLE") s -= 8;
    if (intel.risk.level === "HIGH") s -= 10;
    else if (intel.risk.level === "MEDIUM") s -= 4;
    if (intel.contactability.level === "LOW") s -= 8;
    else if (intel.contactability.level === "MEDIUM") s -= 2;
  }
  const ch = ops?.contactHistory?.status;
  if (ch === "SUPPRESS AUTOMATION") s -= 30;
  if (ch === "NO FURTHER ACTION") s -= 25;
  return Math.max(0, Math.round(s));
}

export function computeQueue(scenarios, intelMap) {
  return scenarios
    .map((s) => ({ id: s.id, name: s.client.name, issue: s.client.issue, qual: intelMap[s.id], ops: OPS_CONFIG[s.id] }))
    .filter((x) => x.qual)
    .map((x) => ({ ...x, rankScore: rankScore(x.qual, x.ops) }))
    .sort((a, b) => b.rankScore - a.rankScore)
    .map((x, i) => ({ ...x, rank: i + 1 }));
}

export const QUEUE_TONES = {
  HOT: "text-orange-400 border-orange-500/40 bg-orange-500/10",
  WARM: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  COOLING: "text-sky-400 border-sky-500/40 bg-sky-500/10",
  STALE: "text-zinc-400 border-zinc-700 bg-zinc-800/60",
  FRESH: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  AGING: "text-amber-400 border-amber-500/40 bg-amber-500/10",
};
